/* eslint-disable radix */
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { formatCurrency } from 'utils';
import { InputLoader } from './InputLoader';
import InputStyles from './InputStyles';

const BACKSPACE_KEY = 'Backspace';
const DELETE_KEY = 'Delete';
const ARROW_LEFT = 'ArrowLeft';
const ARROW_RIGHT = 'ArrowRight';
const NUMBER_KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const CURRENCY_SYMBOLS = ['.', ','];

interface IUIProps {
  className?: string;
  placeholder?: string;
  hasError?: boolean;
  isLoading?: boolean;
  height?: number;
  prefixIsSeparate?: boolean;
  tabIndex?: number;
  width?: number;
  isTotal?: boolean;
  maxCurrencyLength?: number;
  allowZero?: boolean;
  autoFocus?: boolean;
}

interface ILayoutProps extends IUIProps {
  disabled?: boolean;
  id?: string;
  name?: string;
  readOnly?: boolean;
  value?: any;
  onChange(cents: string): void;
}

const Layout = styled.div<Partial<ILayoutProps>>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ width }) => (width ? toRem(width) : '100%')};
  height: ${toRem(theme.input.height.large)};
`;

const Input = styled.input<IUIProps>`
  ${InputStyles}

  ${({ prefixIsSeparate }) =>
    prefixIsSeparate &&
    css`
      padding-left: ${toRem(22)};
      text-align: right;
    `}

  ${({ isTotal }) =>
    isTotal &&
    css`
      font-weight: ${theme.font.weight.medium};
    `}

    ${({ height }) =>
    height &&
    css`
      height: ${toRem(height)};
    `}
`;

export const CentBasedInputCurrency = ({ value, onChange, disabled = false, className, width, isTotal = false, maxCurrencyLength = 6, isLoading, autoFocus = false, allowZero = false, placeholder, hasError, ...rest }: ILayoutProps) => {
  const [cursor, setCursor] = useState<number | undefined>(undefined);
  const [overrideCursor, setOverrideCursor] = useState<boolean>(false);

  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const input = ref.current;
    if (input && cursor && overrideCursor) {
      input.setSelectionRange(cursor, cursor);
      setOverrideCursor(false);
    }
  }, [ref, value, cursor, overrideCursor]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const digits = e.currentTarget.value.replace(/\D/g, '');
    if (digits.length === 0) return;

    onChange(digits);

    if (e.currentTarget.selectionStart && e.currentTarget.selectionStart < e.currentTarget.value.length) {
      setOverrideCursor(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const elem = e.currentTarget;
    const isBackspace = e.key === BACKSPACE_KEY;
    const isDelete = e.key === DELETE_KEY;

    const inputVal = elem.value;
    const cursorPosition = elem.selectionStart || 0;
    const cursorPositionEnd = elem.selectionEnd || 0;
    const specialCount = (inputVal.match(/[^?!0-9]/g) || []).length - 1;

    // handle range selection (CTL+a or CTL+<move_cursor>)
    if (isBackspace || isDelete) {
      if (cursorPosition !== cursorPositionEnd) {
        e.preventDefault();
        const spaces = cursorPositionEnd - cursorPosition;
        const digits = (inputVal.substring(0, cursorPosition) + inputVal.substring(cursorPosition + spaces)).replace(/\D/g, '').substring(0, maxCurrencyLength);
        onChange(digits.length === 0 ? '0' : digits);
        setCursor(cursorPosition);
        setOverrideCursor(true);
        return true;
      }
    }

    // handle single character delete with backspace button (move left)
    if (isBackspace) {
      // do not move cursor if in position 1
      if (cursorPosition === 1 && cursorPosition === cursorPositionEnd) {
        e.preventDefault();
        return true;
      }

      // get character to left of cursor
      const char = elem.value[cursorPosition - 1];

      if (CURRENCY_SYMBOLS.includes(char)) {
        e.preventDefault();
        const digits = (inputVal.substring(0, cursorPosition - 2) + inputVal.substring(cursorPosition, inputVal.length)).replace(/\D/g, ''); // Remove the digit to the right of the cursor position & the special character (,.).
        const removeCount = digits.length < 5 ? (digits.length === 2 ? 0 : 2) : digits.length === 5 ? 3 : 2; // control the remove count based on the length of digits.

        onChange(digits);
        setCursor(cursorPosition - removeCount === 0 ? 1 : cursorPosition - removeCount); // set cursor position
        setOverrideCursor(true);
        return true;
      }

      // Do not move cursor to end if cursor is in pos 0 or 1.
      if (cursorPosition <= 1) {
        e.preventDefault();
        return false;
      }

      // Handle cursor position when deleting digit and the cursor is on the left hand side of a digit.
      const digitLen = inputVal.replace(/\D/g, '').length - 1;
      const pos = digitLen < 3 ? (digitLen <= 2 ? 0 : 1) : digitLen > 5 ? 1 : digitLen === 5 ? 2 : 1;

      setCursor(cursorPosition - pos === 0 ? 1 : cursorPosition - pos);
      return true;
    }

    // handle single character delete with delete button (move right)
    if (isDelete) {
      // move cursor 1 space if in position 0
      if (cursorPosition === 0) {
        e.preventDefault();
        setCursor(1);
        setOverrideCursor(true);
        return true;
      }

      // get character to right of cursor
      const char = elem.value[cursorPosition];

      if (CURRENCY_SYMBOLS.includes(char)) {
        e.preventDefault();
        const digits = (inputVal.substring(0, cursorPosition) + inputVal.substring(cursorPosition + 2, inputVal.length)).replace(/\D/g, ''); // Remove the digit to the right of the cursor position & the special character (,.).
        const digitLength = digits.replace(/\D/g, '').length;
        const removeCount = cursorPosition === 0 ? 0 : digitLength < 3 ? 2 : digitLength > 5 ? 1 : digitLength === 4 || digitLength === 3 ? 1 : 0;

        onChange(digits);
        setCursor(cursorPosition + removeCount); // set cursor position
        setOverrideCursor(true);
        return true;
      }

      // Handle cursor position when deleting digit and the cursor is on the left hand side of a digit.
      const digitLen = inputVal.replace(/\D/g, '').length - 1;
      const pos = digitLen < 3 ? 1 : digitLen > 5 && specialCount === 2 ? 0 : specialCount === 2 ? -1 : 0;
      setCursor(cursorPosition + pos === 0 ? 1 : cursorPosition + pos);
      return true;
    }

    // The following code is used to keep track of the cursor position.
    if (NUMBER_KEYS.includes(e.key)) {
      // Don't do anything if the position is 1 and key value is 0.
      if (cursorPosition === 1 && e.key === '0') {
        e.preventDefault();
        return true;
      }

      const digitsLength = inputVal.replace(/\D/g, '').replace(/^0+/, '').length + 1;
      const pos = cursorPosition === 0 ? 2 : cursorPosition === 1 ? 1 : digitsLength <= 3 ? 0 : digitsLength <= 5 ? 1 : digitsLength === 6 ? 2 : 1;

      const char = elem.value[cursorPosition - 1];
      const offsetChar = CURRENCY_SYMBOLS.includes(char) ? 1 : 0;
      setCursor(cursorPosition + pos - offsetChar);
      return true;
    }

    if (e.key === ARROW_LEFT) {
      setCursor(cursorPosition - 1);
      return true;
    }

    if (e.key === ARROW_RIGHT) {
      setCursor(cursorPosition + 1);
      return true;
    }

    setCursor(cursorPosition);

    return true;
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    // get input element & cursor positions
    const elem = e.currentTarget;
    const cursorPosition = elem.selectionStart || 0;
    const cursorPositionEnd = elem.selectionEnd || 0;

    // get the input value
    const inputVal = elem.value;
    const inputDigits = inputVal.replace(/\D/g, '');

    // get the pasted text and parse out any digits
    const pasteDigits = e.clipboardData.getData('text').replace(/\D/g, '');
    if (pasteDigits.length === 0) return true; // do not paste if no digits in pasted string

    // handle selection range
    const spaces = cursorPositionEnd - cursorPosition;

    // get the start offset based on the number of special characters in the input
    const startOffset =
      CURRENCY_SYMBOLS.includes(inputVal[cursorPosition]) && CURRENCY_SYMBOLS.includes(inputVal[cursorPositionEnd - 1])
        ? 2
        : CURRENCY_SYMBOLS.includes(inputVal[cursorPosition]) || CURRENCY_SYMBOLS.includes(inputVal[cursorPositionEnd - 1])
          ? 1
          : 0;

    const specCharCount =
      CURRENCY_SYMBOLS.includes(inputVal[cursorPosition]) || CURRENCY_SYMBOLS.includes(inputVal[cursorPositionEnd - 1]) ? (inputVal.substring(cursorPosition, cursorPositionEnd).match(/[^?!0-9]/g) || []).length - startOffset : 0;

    onChange((inputVal.substring(0, cursorPosition) + pasteDigits.toString() + inputVal.substring(cursorPosition + spaces)).replace(/\D/g, '').substring(0, maxCurrencyLength));

    if (cursorPosition !== cursorPositionEnd) {
      setCursor(cursorPositionEnd - specCharCount);
    } else {
      setCursor(cursorPositionEnd + inputDigits.length - specCharCount);
    }

    setOverrideCursor(true);
    return true;
  };

  const formatInputValue = (v?: string | number) => {
    if (!v) return '';
    const digits = v.toString().replace(/\D/g, '');
    const isZeroValue = digits.split('').every((digit: string) => digit === '0');
    if (digits.length === 0) return '';
    if (!allowZero && isZeroValue) return '';

    return formatCurrency(digits);
  };

  return isLoading ? (
    <InputLoader width={width} />
  ) : (
    <Layout className={className} width={width}>
      <Input
        ref={ref}
        placeholder={placeholder || '$'}
        type="text"
        inputMode="numeric"
        value={formatInputValue(value)}
        onPaste={handlePaste}
        onChange={handleAmountChange}
        disabled={disabled}
        isTotal={isTotal}
        onKeyDown={handleKeyDown}
        maxLength={maxCurrencyLength + 3}
        autoFocus={autoFocus}
        autoComplete="off"
        hasError={hasError}
        {...rest}
      />
    </Layout>
  );
};

export default CentBasedInputCurrency;
